/*
    Author: Boopathy M

    Keyboard Support:
      * Enter
      * Down Arrow
      * Up Arrow
      * Right Arrow
      * Left Arrow
      * Home
      * End
      * Ctrl + Home
      * Ctrl + End
      * Tab
*/
import { eventKeyConstants } from './eventKey';

export const treeGridHelper = (refID : string = null, cb : any,
  event : any = null, expandable = 0, expanded = false) => {
  const trees = document.getElementById(refID);
  const focusableTreeElements = trees.querySelectorAll(
    '[data-rowtype="obj-rows"]'
  )
  const focusableRowArr = Array.prototype.slice.call(focusableTreeElements);
  const focusRowIndex = focusableRowArr?.indexOf(event.target);
  const currentRole = event.target.attributes['data-rowtype']?.value;

  const syntheticEvents : any = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  }

  const changeTabIndex = (eventTarget: any, nextFocusableEle: any) => {
    if (currentRole === 'obj-rows') {
      eventTarget?.setAttribute('tabindex', '-1');
      nextFocusableEle?.setAttribute('tabindex', '0');
    } else {
      const nextRowToFocus = nextFocusableEle.parentNode?.closest('[data-rowtype="obj-rows"]');
      if (nextRowToFocus) {
        nextRowToFocus?.setAttribute('tabindex', '0');
        const parentRow = eventTarget.parentNode?.closest('[data-rowtype="obj-rows"]');
        parentRow?.setAttribute('tabindex', '-1');
      }
    }
  }

  const getNavColumns = (eventTarget: any) => [...eventTarget.parentNode?.closest('[role="row"]')?.querySelectorAll(
      '[tabindex="-1"]'
    )]

  const focusSameColInDifferentRow = (eventTarget : any, direction: number, isExtreme = false) => {
    const colDataType = eventTarget.attributes['data-coltype']?.value;
    const getAllColElements = trees.querySelectorAll(
      `[data-coltype=${colDataType}]`
    )
    const getAllColElementsArr = Array.prototype.slice.call(getAllColElements)
    const currColIndex = getAllColElementsArr.indexOf(eventTarget);
    const extremeIndex = direction < 0 ? 0 : getAllColElements.length - 1;
    const nextDirectionIndex = currColIndex + direction;
    const finalNextFocusIndex = (getAllColElements.length <= nextDirectionIndex)
      ? currColIndex : nextDirectionIndex;
    const nextFocusIndex = isExtreme ? extremeIndex : (finalNextFocusIndex);
    const nextFocusableEle = getAllColElementsArr[nextFocusIndex];
    if (nextFocusableEle) {
      changeTabIndex(eventTarget, nextFocusableEle);
      nextFocusableEle?.focus();
    }
  }

  const focusRowAndChangeExpanded = (eventTarget : any, direction, focusParent = false) => {
    const currRowColElements = getNavColumns(eventTarget);
    const currIndex = currRowColElements?.indexOf(eventTarget);
    if (!focusParent || currIndex !== 0) {
      currRowColElements[currIndex + direction]?.focus();
    } else if (focusParent) {
      const closestParent = eventTarget.parentNode?.closest('[data-rowtype="obj-rows"]');
      closestParent?.focus();
    }
  }

  const focusFirstChild = (eventTarget) => {
    const currRowColElements = eventTarget.querySelectorAll(
      '[tabindex="-1"]'
    )
    if (currRowColElements) {
      const nextFocusableEle = currRowColElements[0];
      nextFocusableEle.focus();
    }
  }

  const restrictIndex = (rIndex, resNumItems) => {
    if (rIndex < 0) {
      return 0;
    }
    return rIndex >= resNumItems ? rIndex - 1 : rIndex;
  }

  switch (event.key) {
    case eventKeyConstants.ENTER:
      syntheticEvents(event);
      cb();
      break;
    case eventKeyConstants.ARROWRIGHT:
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        if (expandable) {
          if (expanded) {
            focusFirstChild(event.target);
          } else {
            cb();
          }
        } else {
          focusFirstChild(event.target);
        }
      } else {
        focusRowAndChangeExpanded(event.target, 1, false);
      }
      break;
    case eventKeyConstants.ARROWLEFT: {
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        if (!!expandable && expanded) {
          cb();
        } else {
          const ariaLevel = +(event.target.attributes['aria-level']?.value);
          if (ariaLevel > 1) {
            let rowIndex = focusRowIndex;
            let newLevel;
            for (let len = 0; len <= focusRowIndex; len++) {
              rowIndex = restrictIndex(rowIndex - 1, focusableRowArr.length);
              newLevel = +(focusableRowArr[rowIndex].getAttribute('aria-level'));
              if (ariaLevel - 1 === newLevel) break;
            }
            changeTabIndex(event.target, focusableRowArr[rowIndex]);
            focusableRowArr[rowIndex]?.focus();
          }
        }
      } else {
        focusRowAndChangeExpanded(event.target, -1, true);
      }
      break;
    }
    case eventKeyConstants.ARROWUP:
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        const prevEle = event.target.previousSibling;
        if (prevEle) {
          if (prevEle.attributes['data-rowtype']?.value === 'obj-rows') {
            changeTabIndex(event.target, prevEle);
            prevEle.focus();
          } else {
            const correctIndex = focusRowIndex <= 0 ? 0 : focusRowIndex - 1;
            changeTabIndex(event.target, focusableRowArr[correctIndex]);
            focusableRowArr[correctIndex]?.focus();
          }
        }
      } else {
        focusSameColInDifferentRow(event.target, -1);
      }
      break;
    case eventKeyConstants.ARROWDOWN:
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        const nextEle = event.target.nextSibling;
        if (nextEle) {
          if (nextEle.attributes['data-rowtype']?.value === 'obj-rows') {
            changeTabIndex(event.target, nextEle);
            nextEle.focus();
          } else {
            const correctIndex = focusRowIndex >= focusRowIndex.length - 1
              ? focusRowIndex.length - 1 : focusRowIndex + 1;
            changeTabIndex(event.target, focusableRowArr[correctIndex]);
            focusableRowArr[correctIndex]?.focus();
          }
        }
      } else {
        focusSameColInDifferentRow(event.target, 1);
      }
      break;
    case eventKeyConstants.HOME: {
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        changeTabIndex(event.target, focusableTreeElements[0]);
        (focusableTreeElements[0] as HTMLElement).focus();
      } else if (event.ctrlKey) {
        focusSameColInDifferentRow(event.target, -1, true);
      } else {
        const currRowColElements = getNavColumns(event.target);
        currRowColElements[0]?.focus();
      }
      break;
    }
    case eventKeyConstants.END: {
      syntheticEvents(event);
      if (currentRole === 'obj-rows') {
        changeTabIndex(event.target, focusableTreeElements[focusableTreeElements.length - 1]);
        (focusableTreeElements[focusableTreeElements.length - 1] as HTMLElement).focus();
      } else if (event.ctrlKey) {
        focusSameColInDifferentRow(event.target, 1, true);
      } else {
        const currRowColElements = getNavColumns(event.target);
        currRowColElements[currRowColElements.length - 1]?.focus();
      }
      break;
    }
    case eventKeyConstants.TAB:
      if (currentRole === 'obj-rows') {
        if (!event.shiftKey) {
          syntheticEvents(event);
          const currRowColElements = event.target.querySelectorAll(
            '[tabindex="-1"]'
          )
          if (currRowColElements) currRowColElements[0].focus();
        }
      } else {
        syntheticEvents(event);
        if (!event.shiftKey) {
          const nodeEl = document.querySelector<HTMLElement>('.react-modal-okr-popup');
          const modalFocusElements = nodeEl?.querySelectorAll<HTMLElement>(
            'input, [tabindex]:not([tabindex="-1"]), button'
          )
          modalFocusElements[0]?.focus();
        } else {
          event.target.parentNode?.closest('[data-rowtype="obj-rows"]')?.focus();
        }
      }
      break;
    default:
      // not to prevent synthetic events for other keys
      break;
  }
}
